/**
 * Canonical ISO-3166-1 alpha-2 country dataset for the HR platform.
 *
 * Single source of truth for nationality pickers: every country carries an
 * uppercase alpha-2 `code`, an English name, and an Arabic name. Names render
 * from THIS dataset by the active locale — they are NOT stored in i18n bundles
 * (a 249-entry closed list doesn't belong in translation JSON).
 *
 * The `COUNTRY_ALIASES` table maps normalized free-text a user might type
 * (Arabic demonyms, colloquial spellings, common English shorthand like "KSA"
 * / "UAE" / "UK" / "USA") to the canonical code, for `resolveCountry` matching
 * of imported / typed values. Aliases are keyed as raw strings and normalized
 * at load time so authors write natural forms here.
 *
 * Order is roughly MENA + common expat-source countries first (so the picker's
 * default scroll surfaces the likely picks for a Jordan/MENA tenant), then the
 * remainder alphabetically by English name.
 */

export interface Country {
  /** ISO-3166-1 alpha-2, uppercase. Stored as the wire value. */
  code: string;
  nameEn: string;
  nameAr: string;
}

/** MENA + Gulf + frequent expat-source countries, surfaced first. */
const PRIORITY: Country[] = [
  { code: "JO", nameEn: "Jordan", nameAr: "الأردن" },
  { code: "SA", nameEn: "Saudi Arabia", nameAr: "السعودية" },
  { code: "AE", nameEn: "United Arab Emirates", nameAr: "الإمارات" },
  { code: "KW", nameEn: "Kuwait", nameAr: "الكويت" },
  { code: "QA", nameEn: "Qatar", nameAr: "قطر" },
  { code: "BH", nameEn: "Bahrain", nameAr: "البحرين" },
  { code: "OM", nameEn: "Oman", nameAr: "عُمان" },
  { code: "SY", nameEn: "Syria", nameAr: "سوريا" },
  { code: "PS", nameEn: "Palestine", nameAr: "فلسطين" },
  { code: "LB", nameEn: "Lebanon", nameAr: "لبنان" },
  { code: "IQ", nameEn: "Iraq", nameAr: "العراق" },
  { code: "EG", nameEn: "Egypt", nameAr: "مصر" },
  { code: "YE", nameEn: "Yemen", nameAr: "اليمن" },
  { code: "SD", nameEn: "Sudan", nameAr: "السودان" },
  { code: "LY", nameEn: "Libya", nameAr: "ليبيا" },
  { code: "TN", nameEn: "Tunisia", nameAr: "تونس" },
  { code: "DZ", nameEn: "Algeria", nameAr: "الجزائر" },
  { code: "MA", nameEn: "Morocco", nameAr: "المغرب" },
  { code: "PK", nameEn: "Pakistan", nameAr: "باكستان" },
  { code: "IN", nameEn: "India", nameAr: "الهند" },
  { code: "BD", nameEn: "Bangladesh", nameAr: "بنغلاديش" },
  { code: "PH", nameEn: "Philippines", nameAr: "الفلبين" },
  { code: "LK", nameEn: "Sri Lanka", nameAr: "سريلانكا" },
  { code: "NP", nameEn: "Nepal", nameAr: "نيبال" },
  { code: "ID", nameEn: "Indonesia", nameAr: "إندونيسيا" },
  { code: "ET", nameEn: "Ethiopia", nameAr: "إثيوبيا" },
  { code: "KE", nameEn: "Kenya", nameAr: "كينيا" },
  { code: "TR", nameEn: "Turkey", nameAr: "تركيا" },
  { code: "IR", nameEn: "Iran", nameAr: "إيران" },
  { code: "US", nameEn: "United States", nameAr: "الولايات المتحدة" },
  { code: "GB", nameEn: "United Kingdom", nameAr: "المملكة المتحدة" },
];

/** Everything else, ISO-3166-1 alpha-2, alphabetical by English name. */
const REST: Country[] = [
  { code: "AF", nameEn: "Afghanistan", nameAr: "أفغانستان" },
  { code: "AX", nameEn: "Åland Islands", nameAr: "جزر آلاند" },
  { code: "AL", nameEn: "Albania", nameAr: "ألبانيا" },
  { code: "AS", nameEn: "American Samoa", nameAr: "ساموا الأمريكية" },
  { code: "AD", nameEn: "Andorra", nameAr: "أندورا" },
  { code: "AO", nameEn: "Angola", nameAr: "أنغولا" },
  { code: "AI", nameEn: "Anguilla", nameAr: "أنغويلا" },
  { code: "AQ", nameEn: "Antarctica", nameAr: "أنتاركتيكا" },
  { code: "AG", nameEn: "Antigua and Barbuda", nameAr: "أنتيغوا وبربودا" },
  { code: "AR", nameEn: "Argentina", nameAr: "الأرجنتين" },
  { code: "AM", nameEn: "Armenia", nameAr: "أرمينيا" },
  { code: "AW", nameEn: "Aruba", nameAr: "أروبا" },
  { code: "AU", nameEn: "Australia", nameAr: "أستراليا" },
  { code: "AT", nameEn: "Austria", nameAr: "النمسا" },
  { code: "AZ", nameEn: "Azerbaijan", nameAr: "أذربيجان" },
  { code: "BS", nameEn: "Bahamas", nameAr: "الباهاما" },
  { code: "BB", nameEn: "Barbados", nameAr: "بربادوس" },
  { code: "BY", nameEn: "Belarus", nameAr: "بيلاروسيا" },
  { code: "BE", nameEn: "Belgium", nameAr: "بلجيكا" },
  { code: "BZ", nameEn: "Belize", nameAr: "بليز" },
  { code: "BJ", nameEn: "Benin", nameAr: "بنين" },
  { code: "BM", nameEn: "Bermuda", nameAr: "برمودا" },
  { code: "BT", nameEn: "Bhutan", nameAr: "بوتان" },
  { code: "BO", nameEn: "Bolivia", nameAr: "بوليفيا" },
  { code: "BA", nameEn: "Bosnia and Herzegovina", nameAr: "البوسنة والهرسك" },
  { code: "BW", nameEn: "Botswana", nameAr: "بوتسوانا" },
  { code: "BV", nameEn: "Bouvet Island", nameAr: "جزيرة بوفيه" },
  { code: "BR", nameEn: "Brazil", nameAr: "البرازيل" },
  {
    code: "IO",
    nameEn: "British Indian Ocean Territory",
    nameAr: "الإقليم البريطاني في المحيط الهندي",
  },
  { code: "VG", nameEn: "British Virgin Islands", nameAr: "جزر فيرجن البريطانية" },
  { code: "BN", nameEn: "Brunei", nameAr: "بروناي" },
  { code: "BG", nameEn: "Bulgaria", nameAr: "بلغاريا" },
  { code: "BF", nameEn: "Burkina Faso", nameAr: "بوركينا فاسو" },
  { code: "BI", nameEn: "Burundi", nameAr: "بوروندي" },
  { code: "CV", nameEn: "Cabo Verde", nameAr: "الرأس الأخضر" },
  { code: "KH", nameEn: "Cambodia", nameAr: "كمبوديا" },
  { code: "CM", nameEn: "Cameroon", nameAr: "الكاميرون" },
  { code: "CA", nameEn: "Canada", nameAr: "كندا" },
  { code: "BQ", nameEn: "Caribbean Netherlands", nameAr: "هولندا الكاريبية" },
  { code: "KY", nameEn: "Cayman Islands", nameAr: "جزر كايمان" },
  { code: "CF", nameEn: "Central African Republic", nameAr: "جمهورية أفريقيا الوسطى" },
  { code: "TD", nameEn: "Chad", nameAr: "تشاد" },
  { code: "CL", nameEn: "Chile", nameAr: "تشيلي" },
  { code: "CN", nameEn: "China", nameAr: "الصين" },
  { code: "CX", nameEn: "Christmas Island", nameAr: "جزيرة كريسماس" },
  { code: "CC", nameEn: "Cocos (Keeling) Islands", nameAr: "جزر كوكوس (كيلينغ)" },
  { code: "CO", nameEn: "Colombia", nameAr: "كولومبيا" },
  { code: "KM", nameEn: "Comoros", nameAr: "جزر القمر" },
  { code: "CG", nameEn: "Congo", nameAr: "الكونغو" },
  { code: "CD", nameEn: "Congo (DRC)", nameAr: "جمهورية الكونغو الديمقراطية" },
  { code: "CK", nameEn: "Cook Islands", nameAr: "جزر كوك" },
  { code: "CR", nameEn: "Costa Rica", nameAr: "كوستاريكا" },
  { code: "CI", nameEn: "Côte d'Ivoire", nameAr: "ساحل العاج" },
  { code: "HR", nameEn: "Croatia", nameAr: "كرواتيا" },
  { code: "CU", nameEn: "Cuba", nameAr: "كوبا" },
  { code: "CW", nameEn: "Curaçao", nameAr: "كوراساو" },
  { code: "CY", nameEn: "Cyprus", nameAr: "قبرص" },
  { code: "CZ", nameEn: "Czechia", nameAr: "التشيك" },
  { code: "DK", nameEn: "Denmark", nameAr: "الدنمارك" },
  { code: "DJ", nameEn: "Djibouti", nameAr: "جيبوتي" },
  { code: "DM", nameEn: "Dominica", nameAr: "دومينيكا" },
  { code: "DO", nameEn: "Dominican Republic", nameAr: "جمهورية الدومينيكان" },
  { code: "EC", nameEn: "Ecuador", nameAr: "الإكوادور" },
  { code: "SV", nameEn: "El Salvador", nameAr: "السلفادور" },
  { code: "GQ", nameEn: "Equatorial Guinea", nameAr: "غينيا الاستوائية" },
  { code: "ER", nameEn: "Eritrea", nameAr: "إريتريا" },
  { code: "EE", nameEn: "Estonia", nameAr: "إستونيا" },
  { code: "SZ", nameEn: "Eswatini", nameAr: "إسواتيني" },
  { code: "FK", nameEn: "Falkland Islands", nameAr: "جزر فوكلاند" },
  { code: "FO", nameEn: "Faroe Islands", nameAr: "جزر فارو" },
  { code: "FJ", nameEn: "Fiji", nameAr: "فيجي" },
  { code: "FI", nameEn: "Finland", nameAr: "فنلندا" },
  { code: "FR", nameEn: "France", nameAr: "فرنسا" },
  { code: "GF", nameEn: "French Guiana", nameAr: "غويانا الفرنسية" },
  { code: "PF", nameEn: "French Polynesia", nameAr: "بولينيزيا الفرنسية" },
  { code: "TF", nameEn: "French Southern Territories", nameAr: "الأقاليم الجنوبية الفرنسية" },
  { code: "GA", nameEn: "Gabon", nameAr: "الغابون" },
  { code: "GM", nameEn: "Gambia", nameAr: "غامبيا" },
  { code: "GE", nameEn: "Georgia", nameAr: "جورجيا" },
  { code: "DE", nameEn: "Germany", nameAr: "ألمانيا" },
  { code: "GH", nameEn: "Ghana", nameAr: "غانا" },
  { code: "GI", nameEn: "Gibraltar", nameAr: "جبل طارق" },
  { code: "GR", nameEn: "Greece", nameAr: "اليونان" },
  { code: "GL", nameEn: "Greenland", nameAr: "غرينلاند" },
  { code: "GD", nameEn: "Grenada", nameAr: "غرينادا" },
  { code: "GP", nameEn: "Guadeloupe", nameAr: "غوادلوب" },
  { code: "GU", nameEn: "Guam", nameAr: "غوام" },
  { code: "GT", nameEn: "Guatemala", nameAr: "غواتيمالا" },
  { code: "GG", nameEn: "Guernsey", nameAr: "غيرنزي" },
  { code: "GN", nameEn: "Guinea", nameAr: "غينيا" },
  { code: "GW", nameEn: "Guinea-Bissau", nameAr: "غينيا بيساو" },
  { code: "GY", nameEn: "Guyana", nameAr: "غيانا" },
  { code: "HT", nameEn: "Haiti", nameAr: "هايتي" },
  { code: "HM", nameEn: "Heard and McDonald Islands", nameAr: "جزيرة هيرد وجزر ماكدونالد" },
  { code: "HN", nameEn: "Honduras", nameAr: "هندوراس" },
  { code: "HK", nameEn: "Hong Kong", nameAr: "هونغ كونغ" },
  { code: "HU", nameEn: "Hungary", nameAr: "المجر" },
  { code: "IS", nameEn: "Iceland", nameAr: "آيسلندا" },
  { code: "IE", nameEn: "Ireland", nameAr: "أيرلندا" },
  { code: "IM", nameEn: "Isle of Man", nameAr: "جزيرة مان" },
  { code: "IL", nameEn: "Israel", nameAr: "إسرائيل" },
  { code: "IT", nameEn: "Italy", nameAr: "إيطاليا" },
  { code: "JM", nameEn: "Jamaica", nameAr: "جامايكا" },
  { code: "JP", nameEn: "Japan", nameAr: "اليابان" },
  { code: "JE", nameEn: "Jersey", nameAr: "جيرسي" },
  { code: "KZ", nameEn: "Kazakhstan", nameAr: "كازاخستان" },
  { code: "KI", nameEn: "Kiribati", nameAr: "كيريباتي" },
  { code: "KG", nameEn: "Kyrgyzstan", nameAr: "قيرغيزستان" },
  { code: "LA", nameEn: "Laos", nameAr: "لاوس" },
  { code: "LV", nameEn: "Latvia", nameAr: "لاتفيا" },
  { code: "LS", nameEn: "Lesotho", nameAr: "ليسوتو" },
  { code: "LR", nameEn: "Liberia", nameAr: "ليبيريا" },
  { code: "LI", nameEn: "Liechtenstein", nameAr: "ليختنشتاين" },
  { code: "LT", nameEn: "Lithuania", nameAr: "ليتوانيا" },
  { code: "LU", nameEn: "Luxembourg", nameAr: "لوكسمبورغ" },
  { code: "MO", nameEn: "Macao", nameAr: "ماكاو" },
  { code: "MG", nameEn: "Madagascar", nameAr: "مدغشقر" },
  { code: "MW", nameEn: "Malawi", nameAr: "مالاوي" },
  { code: "MY", nameEn: "Malaysia", nameAr: "ماليزيا" },
  { code: "MV", nameEn: "Maldives", nameAr: "المالديف" },
  { code: "ML", nameEn: "Mali", nameAr: "مالي" },
  { code: "MT", nameEn: "Malta", nameAr: "مالطا" },
  { code: "MH", nameEn: "Marshall Islands", nameAr: "جزر مارشال" },
  { code: "MQ", nameEn: "Martinique", nameAr: "جزر المارتينيك" },
  { code: "MR", nameEn: "Mauritania", nameAr: "موريتانيا" },
  { code: "MU", nameEn: "Mauritius", nameAr: "موريشيوس" },
  { code: "YT", nameEn: "Mayotte", nameAr: "مايوت" },
  { code: "MX", nameEn: "Mexico", nameAr: "المكسيك" },
  { code: "FM", nameEn: "Micronesia", nameAr: "ميكرونيزيا" },
  { code: "MD", nameEn: "Moldova", nameAr: "مولدوفا" },
  { code: "MC", nameEn: "Monaco", nameAr: "موناكو" },
  { code: "MN", nameEn: "Mongolia", nameAr: "منغوليا" },
  { code: "ME", nameEn: "Montenegro", nameAr: "الجبل الأسود" },
  { code: "MS", nameEn: "Montserrat", nameAr: "مونتسرات" },
  { code: "MZ", nameEn: "Mozambique", nameAr: "موزمبيق" },
  { code: "MM", nameEn: "Myanmar", nameAr: "ميانمار" },
  { code: "NA", nameEn: "Namibia", nameAr: "ناميبيا" },
  { code: "NR", nameEn: "Nauru", nameAr: "ناورو" },
  { code: "NL", nameEn: "Netherlands", nameAr: "هولندا" },
  { code: "NC", nameEn: "New Caledonia", nameAr: "كاليدونيا الجديدة" },
  { code: "NZ", nameEn: "New Zealand", nameAr: "نيوزيلندا" },
  { code: "NI", nameEn: "Nicaragua", nameAr: "نيكاراغوا" },
  { code: "NE", nameEn: "Niger", nameAr: "النيجر" },
  { code: "NG", nameEn: "Nigeria", nameAr: "نيجيريا" },
  { code: "NU", nameEn: "Niue", nameAr: "نيوي" },
  { code: "NF", nameEn: "Norfolk Island", nameAr: "جزيرة نورفولك" },
  { code: "KP", nameEn: "North Korea", nameAr: "كوريا الشمالية" },
  { code: "MK", nameEn: "North Macedonia", nameAr: "مقدونيا الشمالية" },
  { code: "MP", nameEn: "Northern Mariana Islands", nameAr: "جزر ماريانا الشمالية" },
  { code: "NO", nameEn: "Norway", nameAr: "النرويج" },
  { code: "PW", nameEn: "Palau", nameAr: "بالاو" },
  { code: "PA", nameEn: "Panama", nameAr: "بنما" },
  { code: "PG", nameEn: "Papua New Guinea", nameAr: "بابوا غينيا الجديدة" },
  { code: "PY", nameEn: "Paraguay", nameAr: "باراغواي" },
  { code: "PE", nameEn: "Peru", nameAr: "بيرو" },
  { code: "PN", nameEn: "Pitcairn Islands", nameAr: "جزر بيتكيرن" },
  { code: "PL", nameEn: "Poland", nameAr: "بولندا" },
  { code: "PT", nameEn: "Portugal", nameAr: "البرتغال" },
  { code: "PR", nameEn: "Puerto Rico", nameAr: "بورتوريكو" },
  { code: "RE", nameEn: "Réunion", nameAr: "روينيون" },
  { code: "RO", nameEn: "Romania", nameAr: "رومانيا" },
  { code: "RU", nameEn: "Russia", nameAr: "روسيا" },
  { code: "RW", nameEn: "Rwanda", nameAr: "رواندا" },
  { code: "BL", nameEn: "Saint Barthélemy", nameAr: "سان بارتليمي" },
  { code: "SH", nameEn: "Saint Helena", nameAr: "سانت هيلينا" },
  { code: "KN", nameEn: "Saint Kitts and Nevis", nameAr: "سانت كيتس ونيفيس" },
  { code: "LC", nameEn: "Saint Lucia", nameAr: "سانت لوسيا" },
  { code: "MF", nameEn: "Saint Martin", nameAr: "سان مارتن" },
  { code: "PM", nameEn: "Saint Pierre and Miquelon", nameAr: "سان بيير ومكويلون" },
  { code: "VC", nameEn: "Saint Vincent and the Grenadines", nameAr: "سانت فينسنت والغرينادين" },
  { code: "WS", nameEn: "Samoa", nameAr: "ساموا" },
  { code: "SM", nameEn: "San Marino", nameAr: "سان مارينو" },
  { code: "ST", nameEn: "São Tomé and Príncipe", nameAr: "ساو تومي وبرينسيبي" },
  { code: "SN", nameEn: "Senegal", nameAr: "السنغال" },
  { code: "RS", nameEn: "Serbia", nameAr: "صربيا" },
  { code: "SC", nameEn: "Seychelles", nameAr: "سيشل" },
  { code: "SL", nameEn: "Sierra Leone", nameAr: "سيراليون" },
  { code: "SG", nameEn: "Singapore", nameAr: "سنغافورة" },
  { code: "SX", nameEn: "Sint Maarten", nameAr: "سانت مارتن" },
  { code: "SK", nameEn: "Slovakia", nameAr: "سلوفاكيا" },
  { code: "SI", nameEn: "Slovenia", nameAr: "سلوفينيا" },
  { code: "SB", nameEn: "Solomon Islands", nameAr: "جزر سليمان" },
  { code: "SO", nameEn: "Somalia", nameAr: "الصومال" },
  { code: "ZA", nameEn: "South Africa", nameAr: "جنوب أفريقيا" },
  {
    code: "GS",
    nameEn: "South Georgia and the South Sandwich Islands",
    nameAr: "جورجيا الجنوبية وجزر ساندويتش الجنوبية",
  },
  { code: "KR", nameEn: "South Korea", nameAr: "كوريا الجنوبية" },
  { code: "SS", nameEn: "South Sudan", nameAr: "جنوب السودان" },
  { code: "ES", nameEn: "Spain", nameAr: "إسبانيا" },
  { code: "SR", nameEn: "Suriname", nameAr: "سورينام" },
  { code: "SJ", nameEn: "Svalbard and Jan Mayen", nameAr: "سفالبارد وجان ماين" },
  { code: "SE", nameEn: "Sweden", nameAr: "السويد" },
  { code: "CH", nameEn: "Switzerland", nameAr: "سويسرا" },
  { code: "TW", nameEn: "Taiwan", nameAr: "تايوان" },
  { code: "TJ", nameEn: "Tajikistan", nameAr: "طاجيكستان" },
  { code: "TZ", nameEn: "Tanzania", nameAr: "تنزانيا" },
  { code: "TH", nameEn: "Thailand", nameAr: "تايلاند" },
  { code: "TL", nameEn: "Timor-Leste", nameAr: "تيمور الشرقية" },
  { code: "TG", nameEn: "Togo", nameAr: "توغو" },
  { code: "TK", nameEn: "Tokelau", nameAr: "توكيلاو" },
  { code: "TO", nameEn: "Tonga", nameAr: "تونغا" },
  { code: "TT", nameEn: "Trinidad and Tobago", nameAr: "ترينيداد وتوباغو" },
  { code: "TM", nameEn: "Turkmenistan", nameAr: "تركمانستان" },
  { code: "TC", nameEn: "Turks and Caicos Islands", nameAr: "جزر توركس وكايكوس" },
  { code: "TV", nameEn: "Tuvalu", nameAr: "توفالو" },
  { code: "UM", nameEn: "U.S. Outlying Islands", nameAr: "جزر الولايات المتحدة النائية" },
  { code: "VI", nameEn: "U.S. Virgin Islands", nameAr: "جزر فيرجن الأمريكية" },
  { code: "UG", nameEn: "Uganda", nameAr: "أوغندا" },
  { code: "UA", nameEn: "Ukraine", nameAr: "أوكرانيا" },
  { code: "UY", nameEn: "Uruguay", nameAr: "الأوروغواي" },
  { code: "UZ", nameEn: "Uzbekistan", nameAr: "أوزبكستان" },
  { code: "VU", nameEn: "Vanuatu", nameAr: "فانواتو" },
  { code: "VA", nameEn: "Vatican City", nameAr: "الفاتيكان" },
  { code: "VE", nameEn: "Venezuela", nameAr: "فنزويلا" },
  { code: "VN", nameEn: "Vietnam", nameAr: "فيتنام" },
  { code: "WF", nameEn: "Wallis and Futuna", nameAr: "جزر والس وفوتونا" },
  { code: "EH", nameEn: "Western Sahara", nameAr: "الصحراء الغربية" },
  { code: "ZM", nameEn: "Zambia", nameAr: "زامبيا" },
  { code: "ZW", nameEn: "Zimbabwe", nameAr: "زيمبابوي" },
];

/**
 * Full ordered country list. PRIORITY first (MENA + common expat sources),
 * then the alphabetical remainder. Frozen so consumers can't mutate the SSOT.
 */
export const COUNTRIES: readonly Country[] = Object.freeze([...PRIORITY, ...REST]);

/** Set of valid uppercase alpha-2 codes — the form-level refinement gate. */
export const COUNTRY_ALPHA2_SET: ReadonlySet<string> = new Set(COUNTRIES.map((c) => c.code));

/**
 * Alias table: raw free-text (Arabic demonyms / colloquial spellings / English
 * shorthand) → canonical alpha-2 code. Keys are normalized at load in
 * `resolveCountry` so authors write natural forms. Multiple aliases per country
 * are fine; ambiguity only arises when two DIFFERENT codes share a normalized
 * key (guarded against by the dataset-integrity test).
 */
export const COUNTRY_ALIASES: ReadonlyArray<readonly [alias: string, code: string]> = [
  // Jordan
  ["أردني", "JO"],
  ["اردني", "JO"],
  ["أردنية", "JO"],
  ["اردن", "JO"],
  ["jordanian", "JO"],
  // Saudi Arabia
  ["سعودي", "SA"],
  ["سعودية", "SA"],
  ["ksa", "SA"],
  ["saudi", "SA"],
  ["saudi arabian", "SA"],
  // United Arab Emirates
  ["اماراتي", "AE"],
  ["إماراتي", "AE"],
  ["اماراتية", "AE"],
  ["امارات", "AE"],
  ["uae", "AE"],
  ["emirati", "AE"],
  ["emirates", "AE"],
  // Kuwait
  ["كويتي", "KW"],
  ["كويتية", "KW"],
  ["kuwaiti", "KW"],
  // Qatar
  ["قطري", "QA"],
  ["قطرية", "QA"],
  ["qatari", "QA"],
  // Bahrain
  ["بحريني", "BH"],
  ["بحرينية", "BH"],
  ["bahraini", "BH"],
  // Oman
  ["عماني", "OM"],
  ["عمانية", "OM"],
  ["عمان", "OM"],
  ["omani", "OM"],
  // Syria
  ["سوري", "SY"],
  ["سورية", "SY"],
  ["syrian", "SY"],
  // Palestine
  ["فلسطيني", "PS"],
  ["فلسطينية", "PS"],
  ["palestinian", "PS"],
  // Lebanon
  ["لبناني", "LB"],
  ["لبنانية", "LB"],
  ["lebanese", "LB"],
  // Iraq
  ["عراقي", "IQ"],
  ["عراقية", "IQ"],
  ["iraqi", "IQ"],
  // Egypt
  ["مصري", "EG"],
  ["مصرية", "EG"],
  ["egyptian", "EG"],
  // Yemen
  ["يمني", "YE"],
  ["يمنية", "YE"],
  ["yemeni", "YE"],
  // Sudan
  ["سوداني", "SD"],
  ["سودانية", "SD"],
  ["sudanese", "SD"],
  // Libya
  ["ليبي", "LY"],
  ["ليبية", "LY"],
  ["libyan", "LY"],
  // Tunisia
  ["تونسي", "TN"],
  ["تونسية", "TN"],
  ["tunisian", "TN"],
  // Algeria
  ["جزائري", "DZ"],
  ["جزائرية", "DZ"],
  ["algerian", "DZ"],
  // Morocco
  ["مغربي", "MA"],
  ["مغربية", "MA"],
  ["moroccan", "MA"],
  // Pakistan
  ["باكستاني", "PK"],
  ["باكستانية", "PK"],
  ["pakistani", "PK"],
  // India
  ["هندي", "IN"],
  ["هندية", "IN"],
  ["indian", "IN"],
  // Bangladesh
  ["بنغلاديشي", "BD"],
  ["بنغلاديشية", "BD"],
  ["بنغالي", "BD"],
  ["bangladeshi", "BD"],
  // Philippines
  ["فلبيني", "PH"],
  ["فلبينية", "PH"],
  ["filipino", "PH"],
  ["filipina", "PH"],
  // Sri Lanka
  ["سريلانكي", "LK"],
  ["سريلانكية", "LK"],
  ["sri lankan", "LK"],
  // Nepal
  ["نيبالي", "NP"],
  ["نيبالية", "NP"],
  ["nepali", "NP"],
  // Indonesia
  ["إندونيسي", "ID"],
  ["إندونيسية", "ID"],
  ["indonesian", "ID"],
  // Ethiopia
  ["إثيوبي", "ET"],
  ["اثيوبي", "ET"],
  ["إثيوبية", "ET"],
  ["ethiopian", "ET"],
  // Kenya
  ["كيني", "KE"],
  ["كينية", "KE"],
  ["kenyan", "KE"],
  // Turkey
  ["تركي", "TR"],
  ["تركية", "TR"],
  ["turkish", "TR"],
  // Iran
  ["إيراني", "IR"],
  ["ايراني", "IR"],
  ["إيرانية", "IR"],
  ["iranian", "IR"],
  // United States
  ["أمريكي", "US"],
  ["امريكي", "US"],
  ["أمريكية", "US"],
  ["امريكا", "US"],
  ["usa", "US"],
  ["american", "US"],
  ["united states of america", "US"],
  // United Kingdom
  ["بريطاني", "GB"],
  ["بريطانية", "GB"],
  ["uk", "GB"],
  ["british", "GB"],
  ["britain", "GB"],
  ["great britain", "GB"],
  ["england", "GB"],
  // Canada
  ["كندي", "CA"],
  ["كندية", "CA"],
  ["canadian", "CA"],
  // Germany
  ["ألماني", "DE"],
  ["ألمانية", "DE"],
  ["german", "DE"],
  // France
  ["فرنسي", "FR"],
  ["فرنسية", "FR"],
  ["french", "FR"],
  // China
  ["صيني", "CN"],
  ["صينية", "CN"],
  ["chinese", "CN"],
];
